import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, subtotal } = useCart();
  const navigate = useNavigate();
  const shipping = subtotal >= 250 || subtotal === 0 ? 0 : 15;

  const goCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        data-testid="cart-drawer"
        className="bg-bone border-l border-line w-full sm:max-w-md flex flex-col p-0"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-line">
          <SheetTitle className="font-display text-2xl font-semibold text-ink text-left">
            Your Cart
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8 gap-4">
            <div className="w-16 h-16 rounded-full bg-sand flex items-center justify-center">
              <ShoppingBag className="w-7 h-7 text-stone" strokeWidth={1.25} />
            </div>
            <p className="text-stone">Your cart is empty.</p>
            <button
              data-testid="cart-empty-shop-btn"
              onClick={() => { setOpen(false); navigate("/shop"); }}
              className="rounded-full bg-forest text-[#14100F] px-6 py-2.5 text-sm font-semibold hover:bg-forest-dark transition-colors"
            >
              Explore Peptides
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
              {items.map((item) => (
                <div key={item.slug} data-testid={`cart-item-${item.slug}`} className="flex gap-4">
                  <div className="w-20 h-24 rounded-lg bg-sand overflow-hidden flex-shrink-0">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <p className="font-display text-lg text-ink leading-tight">{item.name}</p>
                      <button
                        data-testid={`cart-remove-${item.slug}`}
                        onClick={() => removeItem(item.slug)}
                        className="text-stone hover:text-destructive transition-colors"
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </div>
                    <p className="text-sm text-stone mt-0.5">${item.price.toFixed(2)}</p>
                    <div className="mt-3 inline-flex items-center border border-line rounded-full">
                      <button
                        data-testid={`cart-dec-${item.slug}`}
                        onClick={() => updateQty(item.slug, item.quantity - 1)}
                        className="p-2 hover:text-forest transition-colors"
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="w-8 text-center text-sm font-semibold">{item.quantity}</span>
                      <button
                        data-testid={`cart-inc-${item.slug}`}
                        onClick={() => updateQty(item.slug, item.quantity + 1)}
                        className="p-2 hover:text-forest transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-line px-6 py-5 space-y-4 bg-sand/50">
              <div className="flex justify-between text-sm text-stone">
                <span>Subtotal</span>
                <span data-testid="cart-subtotal" className="text-ink font-semibold">${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm text-stone">
                <span>Shipping</span>
                <span className="text-ink font-semibold">{shipping === 0 ? "Free" : `$${shipping.toFixed(2)}`}</span>
              </div>
              {subtotal > 0 && subtotal < 250 && (
                <p className="text-xs text-forest">Add ${(250 - subtotal).toFixed(2)} more for free shipping.</p>
              )}
              <button
                data-testid="cart-checkout-btn"
                onClick={goCheckout}
                className="w-full rounded-full bg-forest text-[#14100F] py-3.5 text-sm font-semibold hover:bg-forest-dark active:scale-[0.99] transition-[background-color,transform] duration-200"
              >
                Checkout · ${(subtotal + shipping).toFixed(2)}
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
